import React, { useState } from 'react';
import { Plus, GitBranch, ChevronDown, ChevronUp, ArrowRight, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Condition operators supported for step routing.
 * Each operator has a label and whether it requires a value input.
 */
const OPERATORS = [
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'not_equals', label: 'does not equal', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'not_contains', label: 'does not contain', needsValue: true },
  { value: 'is_empty', label: 'is empty', needsValue: false },
  { value: 'is_not_empty', label: 'is not empty', needsValue: false },
  { value: 'greater_than', label: 'is greater than', needsValue: true },
  { value: 'less_than', label: 'is less than', needsValue: true },
];

/**
 * Content-only field types that can't be used in conditions
 * (they don't collect data from respondents).
 */
const CONTENT_FIELD_TYPES = ['heading', 'paragraph', 'image'];

/**
 * Field types that have predefined option lists — for these, we show
 * a dropdown of options instead of a free-text value input.
 */
const OPTION_FIELD_TYPES = ['select', 'radio', 'checkbox'];

/**
 * StepConditionEditor — Inline editor for step-level routing conditions.
 *
 * Props:
 *  - step:       The step object being configured.
 *  - stepIndex:  Index of this step in the steps array.
 *  - steps:      The full array of steps.
 *  - onChange:    Callback: (updatedStep) => void. Called when conditions change.
 */
export default function StepConditionEditor({ step, stepIndex, steps, onChange }) {
  const [expanded, setExpanded] = useState(false);

  // Collect all data-entry fields from *previous* steps (steps that come before this one)
  // so users can create conditions based on answers already collected.
  const previousFields = [];
  for (let i = 0; i < stepIndex; i++) {
    const s = steps[i];
    (s.fields || []).forEach(f => {
      if (!CONTENT_FIELD_TYPES.includes(f.type)) {
        previousFields.push({ ...f, stepTitle: s.title, stepIndex: i });
      }
    });
  }

  // Also include fields from the current step for self-routing conditions
  (step.fields || []).forEach(f => {
    if (!CONTENT_FIELD_TYPES.includes(f.type)) {
      previousFields.push({ ...f, stepTitle: step.title, stepIndex });
    }
  });

  // Build a list of possible target steps (all steps except the current one)
  const targetSteps = steps
    .map((s, idx) => ({ id: s.id, title: s.title, index: idx }))
    .filter((_, idx) => idx !== stepIndex);

  const conditions = step.conditions || [];
  const hasConditions = conditions.length > 0;

  // ── Mutation helpers ────────────────────────────────────────

  const updateConditions = (newConditions) => {
    onChange({ ...step, conditions: newConditions });
  };

  const addCondition = () => {
    const newCondition = {
      id: uuidv4(),
      fieldId: previousFields.length > 0 ? previousFields[0].id : '',
      operator: 'equals',
      value: '',
      targetStepId: targetSteps.length > 0 ? targetSteps[0].id : '',
    };
    updateConditions([...conditions, newCondition]);
    if (!expanded) setExpanded(true);
  };

  const updateCondition = (condId, updates) => {
    updateConditions(
      conditions.map(c => (c.id === condId ? { ...c, ...updates } : c))
    );
  };

  const removeCondition = (condId) => {
    updateConditions(conditions.filter(c => c.id !== condId));
  };

  const updateDefaultNextStep = (targetStepId) => {
    onChange({ ...step, defaultNextStepId: targetStepId || null });
  };

  // ── Early return for first step ─────────────────────────────
  // The first step can't have incoming conditions (there's nothing before it),
  // but it CAN define where to go next, so we still render for it.

  if (previousFields.length === 0 && stepIndex === 0 && steps.length <= 1) {
    return null; // Single-step form — no routing possible
  }

  return (
    <div className="mt-2">
      {/* Toggle button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs transition-colors ${
          hasConditions
            ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <span className="flex items-center gap-1.5">
          <GitBranch className="w-3 h-3" />
          {hasConditions
            ? `${conditions.length} condition${conditions.length !== 1 ? 's' : ''}`
            : 'Add logic'}
        </span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {/* Expanded condition editor */}
      {expanded && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
          {/* Existing conditions */}
          {conditions.map((cond, idx) => (
            <ConditionRow
              key={cond.id}
              condition={cond}
              index={idx}
              previousFields={previousFields}
              targetSteps={targetSteps}
              onUpdate={(updates) => updateCondition(cond.id, updates)}
              onRemove={() => removeCondition(cond.id)}
            />
          ))}

          {/* Add condition button */}
          {previousFields.length > 0 ? (
            <button
              onClick={addCondition}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:border-brand-300 dark:hover:border-brand-500 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add condition
            </button>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-1">
              Add fields to previous steps to create conditions.
            </p>
          )}

          {/* Default / fallback routing */}
          {(conditions.length > 0 || stepIndex < steps.length - 1) && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {conditions.length > 0 ? 'Otherwise, go to:' : 'After this step, go to:'}
                </span>
                <select
                  value={step.defaultNextStepId || ''}
                  onChange={e => updateDefaultNextStep(e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Next step (default)</option>
                  {targetSteps.map(ts => (
                    <option key={ts.id} value={ts.id}>
                      {ts.title} (Step {ts.index + 1})
                    </option>
                  ))}
                  <option value="__submit__">Submit form</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Single condition row: "If [field] [operator] [value] → go to [step]"
 */
function ConditionRow({ condition, index, previousFields, targetSteps, onUpdate, onRemove }) {
  const selectedField = previousFields.find(f => f.id === condition.fieldId);
  const operator = OPERATORS.find(o => o.value === condition.operator) || OPERATORS[0];
  const isOptionField = selectedField && OPTION_FIELD_TYPES.includes(selectedField.type);

  return (
    <div className="relative group bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
      {/* Remove button */}
      <button
        onClick={onRemove}
        className="absolute top-2 right-2 p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove condition"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Row label */}
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {index === 0 ? 'If' : 'Or if'}
      </div>

      {/* Field selector */}
      <div className="grid grid-cols-1 gap-2">
        <select
          value={condition.fieldId}
          onChange={e => onUpdate({ fieldId: e.target.value, value: '' })}
          className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="" disabled>Select a field...</option>
          {previousFields.map(f => (
            <option key={`${f.stepIndex}-${f.id}`} value={f.id}>
              {f.stepTitle}: {f.label}
            </option>
          ))}
        </select>

        {/* Operator selector */}
        <select
          value={condition.operator}
          onChange={e => onUpdate({ operator: e.target.value })}
          className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          {OPERATORS.map(op => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </select>

        {/* Value input — dropdown for option fields, text input otherwise */}
        {operator.needsValue && (
          isOptionField ? (
            <select
              value={condition.value}
              onChange={e => onUpdate({ value: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">Select a value...</option>
              {(selectedField.options || []).map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <input
              type={selectedField?.type === 'number' ? 'number' : 'text'}
              value={condition.value}
              onChange={e => onUpdate({ value: e.target.value })}
              placeholder="Enter value..."
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
            />
          )
        )}
      </div>

      {/* Target step */}
      <div className="flex items-center gap-2 pt-1">
        <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Go to:</span>
        <select
          value={condition.targetStepId}
          onChange={e => onUpdate({ targetStepId: e.target.value })}
          className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="" disabled>Select step...</option>
          {targetSteps.map(ts => (
            <option key={ts.id} value={ts.id}>
              {ts.title} (Step {ts.index + 1})
            </option>
          ))}
          <option value="__submit__">Submit form</option>
        </select>
      </div>
    </div>
  );
}

/**
 * evaluateStepConditions — Runtime evaluation for step routing.
 *
 * Given the current step's conditions and the respondent's answers,
 * returns the ID of the step to navigate to, or null if the default
 * (sequential) behavior should apply.
 *
 * @param {Object} step - The current step object (with conditions array).
 * @param {Object} responses - Map of fieldId → respondent's answer value.
 * @param {Set} [visitedStepIds] - Optional set of already-visited step IDs to prevent cycles.
 * @returns {{ targetStepId: string|null, shouldSubmit: boolean }}
 */
export function evaluateStepConditions(step, responses, visitedStepIds) {
  const conditions = step.conditions || [];

  // Evaluate each condition in order; first match wins
  for (const cond of conditions) {
    if (!cond.fieldId || !cond.operator) continue;

    const answer = responses[cond.fieldId];
    const answerStr = answer != null ? String(answer) : '';
    const valueStr = cond.value != null ? String(cond.value) : '';

    let matched = false;
    switch (cond.operator) {
      case 'equals':
        // For checkbox (array) fields, check if the value is one of the selected options
        if (Array.isArray(answer)) {
          matched = answer.includes(valueStr);
        } else {
          matched = answerStr.toLowerCase() === valueStr.toLowerCase();
        }
        break;
      case 'not_equals':
        if (Array.isArray(answer)) {
          matched = !answer.includes(valueStr);
        } else {
          matched = answerStr.toLowerCase() !== valueStr.toLowerCase();
        }
        break;
      case 'contains':
        if (Array.isArray(answer)) {
          matched = answer.some(a => String(a).toLowerCase().includes(valueStr.toLowerCase()));
        } else {
          matched = answerStr.toLowerCase().includes(valueStr.toLowerCase());
        }
        break;
      case 'not_contains':
        if (Array.isArray(answer)) {
          matched = !answer.some(a => String(a).toLowerCase().includes(valueStr.toLowerCase()));
        } else {
          matched = !answerStr.toLowerCase().includes(valueStr.toLowerCase());
        }
        break;
      case 'is_empty':
        if (Array.isArray(answer)) {
          matched = answer.length === 0;
        } else {
          matched = !answerStr.trim();
        }
        break;
      case 'is_not_empty':
        if (Array.isArray(answer)) {
          matched = answer.length > 0;
        } else {
          matched = !!answerStr.trim();
        }
        break;
      case 'greater_than': {
        const a = parseFloat(answerStr), v = parseFloat(valueStr);
        matched = !isNaN(a) && !isNaN(v) && a > v;
        break;
      }
      case 'less_than': {
        const a = parseFloat(answerStr), v = parseFloat(valueStr);
        matched = !isNaN(a) && !isNaN(v) && a < v;
        break;
      }
      default:
        break;
    }

    if (matched && cond.targetStepId) {
      if (cond.targetStepId === '__submit__') {
        return { targetStepId: null, shouldSubmit: true };
      }
      // Skip this condition if it would create a cycle (route to an already-visited step)
      if (visitedStepIds && visitedStepIds.has(cond.targetStepId)) {
        continue;
      }
      return { targetStepId: cond.targetStepId, shouldSubmit: false };
    }
  }

  // No condition matched — check for a default override
  if (step.defaultNextStepId) {
    if (step.defaultNextStepId === '__submit__') {
      return { targetStepId: null, shouldSubmit: true };
    }
    // Skip default if it would create a cycle
    if (!(visitedStepIds && visitedStepIds.has(step.defaultNextStepId))) {
      return { targetStepId: step.defaultNextStepId, shouldSubmit: false };
    }
  }

  // Fall through to sequential navigation
  return { targetStepId: null, shouldSubmit: false };
}
